import React, {useState, useRef, useCallback, useEffect, useMemo} from 'react';
import {
  StyleSheet,
  TouchableOpacity,
  View,
  Animated,
  Image,
  ViewStyle,
  PanResponder,
  Text,
  ImageBackground,
  PermissionsAndroid,
  ActivityIndicator, // 添加 ImageBackground 导入
  DeviceEventEmitter, // 添加这行
  Dimensions,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  WebviewCustomerMemo,
  WebviewCustomerRef,
} from '../../../components/WebviewCustomer';
import {middlewareType} from '../../../utils/webviewJSBridge';
import {createStyles} from '../../../utils/createStyles';
import {AIWebviewURL} from '../../../env/webviewUrl';
import AINativeModules from '../../../hooks/AINativeModules';
import {getSchedule, GetScheduleData} from '../../../API/newHome';
import ScheduleTimeline from './ScheduleTimeline';
import {useAppDispatch} from '../../../store/hooks';
import {logApi} from '../../../API/base';
import {fetchQuestionsByRecordId} from '../../../store/xueke';
import Toast from 'react-native-toast-message';
import {hideGlobalLoading} from '../../../components/Loading/GlobalLoading';
import IntentLauncher, {
  IntentConstant,
} from '@suxueweibo/react-native-intent-launcher';
import {useNavigation} from '@react-navigation/native';
// import NotificationService from '../../../utils/NotificationManager';
const LEFT_DRAWER_WIDTH = '42rem';
const RIGHT_DRAWER_WIDTH = '21rem';
const LEFT_BUTTON_Y_KEY = 'leftButtonY';
const RIGHT_BUTTON_Y_KEY = 'rightButtonY';

interface ButtonPosition {
  x: string | number;
  y: string | number;
}

interface FloatAIJumpProps {
  leftButtonPosition?: ButtonPosition;
  rightButtonPosition?: ButtonPosition;
  showLeftButton?: boolean;
  showRightButton?: boolean;
}

// 添加日期格式化函数
const formatDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}年${month}月${day}日`;
};

export default function FloatAIJump({
  leftButtonPosition,
  rightButtonPosition,
  showLeftButton = true,
  showRightButton = true,
}: FloatAIJumpProps): React.JSX.Element {
  const navigation = useNavigation();
  const [leftIsOpen, setLeftIsOpen] = useState(false);
  const [rightIsOpen, setRightIsOpen] = useState(false);
  const leftSlideAnim = useRef(
    new Animated.Value(-Dimensions.get('window').height),
  ).current;
  const rightSlideAnim = useRef(
    new Animated.Value(-Dimensions.get('window').height),
  ).current;
  const leftButtonOpacity = useRef(new Animated.Value(1)).current;
  const rightButtonOpacity = useRef(new Animated.Value(1)).current;
  const overlayOpacity = useRef(new Animated.Value(0)).current;
  const leftWebviewRef = useRef<WebviewCustomerRef | null>(null);
  const [leftLoading, setLeftLoading] = useState(true);
  const [leftButtonY, setLeftButtonY] = useState(
    new Animated.Value(Number(leftButtonPosition?.y) || 8),
  );
  const [rightButtonY, setRightButtonY] = useState(
    new Animated.Value(Number(rightButtonPosition?.y) || 8),
  );
  const dispatch = useAppDispatch();
  // 修改日期状态初始化
  const [currentDate, setCurrentDate] = useState(formatDate());
  const [screenDimensions, setScreenDimensions] = useState({
    width: Dimensions.get('window').width,
    height: Dimensions.get('window').height,
  });

  // 添加 scheduleData 状态
  const [scheduleData, setScheduleData] = useState<
    GetScheduleData['scheduleList']
  >([]);

  // 监听屏幕尺寸变化
  useEffect(() => {
    const updateDimensions = () => {
      setScreenDimensions({
        width: Dimensions.get('window').width,
        height: Dimensions.get('window').height,
      });
    };

    const subscription = Dimensions.addEventListener(
      'change',
      updateDimensions,
    );

    return () => {
      subscription.remove();
    };
  }, []);

  // 获取按钮尺寸
  const buttonHeight = 35; // 3.5rem 转换为像素
  const buttonWidth = 86.5; // 8.65rem 转换为像素

  // 修改 panResponderLeft
  const panResponderLeft = PanResponder.create({
    onStartShouldSetPanResponder: () => false,
    onMoveShouldSetPanResponder: (_, gestureState) => {
      return Math.abs(gestureState.dy) > 10;
    },
    onPanResponderMove: (_, gestureState) => {
      // 限制在屏幕范围内
      const newY = Math.max(
        0,
        Math.min(gestureState.moveY, screenDimensions.height - buttonHeight),
      );
      leftButtonY.setValue(newY);
    },
    onPanResponderRelease: (_, gestureState) => {
      const finalY = Math.max(
        0,
        Math.min(gestureState.moveY, screenDimensions.height - buttonHeight),
      );
      saveButtonPosition(LEFT_BUTTON_Y_KEY, finalY);
    },
  });

  // 修改 panResponderRight
  const panResponderRight = PanResponder.create({
    onStartShouldSetPanResponder: () => false,
    onMoveShouldSetPanResponder: (_, gestureState) => {
      return Math.abs(gestureState.dy) > 10;
    },
    onPanResponderMove: (_, gestureState) => {
      // 限制在屏幕范围内
      const newY = Math.max(
        0,
        Math.min(gestureState.moveY, screenDimensions.height - buttonHeight),
      );
      rightButtonY.setValue(newY);
    },
    onPanResponderRelease: (_, gestureState) => {
      const finalY = Math.max(
        0,
        Math.min(gestureState.moveY, screenDimensions.height - buttonHeight),
      );
      saveButtonPosition(RIGHT_BUTTON_Y_KEY, finalY);
    },
  });

  // 修改 loadButtonPositions 函数，确保初始位置也在屏幕范围内
  useEffect(() => {
    const loadButtonPositions = async () => {
      try {
        const savedLeftY = await AsyncStorage.getItem(LEFT_BUTTON_Y_KEY);
        const savedRightY = await AsyncStorage.getItem(RIGHT_BUTTON_Y_KEY);

        if (savedLeftY !== null) {
          const y = Math.max(
            0,
            Math.min(
              parseFloat(savedLeftY),
              screenDimensions.height - buttonHeight,
            ),
          );
          setLeftButtonY(new Animated.Value(y));
        }
        if (savedRightY !== null) {
          const y = Math.max(
            0,
            Math.min(
              parseFloat(savedRightY),
              screenDimensions.height - buttonHeight,
            ),
          );
          setRightButtonY(new Animated.Value(y));
        }
      } catch (error) {
        console.error('Error loading button positions:', error);
      }
    };

    loadButtonPositions();
  }, [screenDimensions.height]);

  // 修改左侧按钮样式
  const leftButtonContainerStyle = React.useMemo(() => {
    const baseStyle = leftButtonPosition
      ? {
          position: 'absolute',
          left: 0, // 固定在左侧边缘
          top: leftButtonPosition.y,
          zIndex: leftIsOpen ? 1002 : 1003,
        }
      : styles.leftButtonContainer;
    return [
      baseStyle,
      {
        opacity: leftButtonOpacity,
        transform: [{translateY: leftButtonY}],
        pointerEvents: leftIsOpen ? 'none' : 'auto',
      },
    ];
  }, [leftButtonPosition, leftIsOpen, leftButtonOpacity, leftButtonY]);

  // 修改右侧按钮样式
  const rightButtonContainerStyle = React.useMemo(() => {
    const baseStyle = rightButtonPosition
      ? {
          position: 'absolute',
          right: 0, // 固定在右侧边缘
          top: rightButtonPosition.y,
          zIndex: rightIsOpen ? 1002 : 1003,
        }
      : styles.rightButtonContainer;
    return [
      baseStyle,
      {
        opacity: rightButtonOpacity,
        transform: [{translateY: rightButtonY}],
        pointerEvents: rightIsOpen ? 'none' : 'auto',
      },
    ];
  }, [rightButtonPosition, rightIsOpen, rightButtonOpacity, rightButtonY]);

  const saveButtonPosition = async (key: string, value: number) => {
    try {
      await AsyncStorage.setItem(key, value.toString());
    } catch (error) {
      console.error('Error saving button position:', error);
    }
  };

  const animateDrawer = useCallback(
    (
      slideAnim: Animated.Value,
      buttonOpacity: Animated.Value,
      toValue: number,
      buttonToValue: number,
      callback?: () => void,
    ) => {
      // 使用屏幕高度作为隐藏位置
      const hidePosition = -Dimensions.get('window').height;

      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: toValue === -1000 ? hidePosition : 0, // 修改这里，使用屏幕高度
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(buttonOpacity, {
          toValue: buttonToValue,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start(callback);
    },
    [],
  );
  const updateOverlay = useCallback(() => {
    Animated.timing(overlayOpacity, {
      toValue: leftIsOpen || rightIsOpen ? 0.5 : 0,
      duration: 300,
      useNativeDriver: true,
    }).start();
  }, [leftIsOpen, rightIsOpen, overlayOpacity]);

  const closeDrawers = useCallback(() => {
    if (leftIsOpen) {
      animateDrawer(leftSlideAnim, leftButtonOpacity, -1000, 1, () =>
        setLeftIsOpen(false),
      );
    }
    if (rightIsOpen) {
      animateDrawer(rightSlideAnim, rightButtonOpacity, -1000, 1, () =>
        setRightIsOpen(false),
      );
    }
  }, [
    leftIsOpen,
    rightIsOpen,
    leftSlideAnim,
    rightSlideAnim,
    leftButtonOpacity,
    rightButtonOpacity,
    animateDrawer,
  ]);

  // 将 formatDateToYYYYMMDD 改为普通函数
  const formatDateToYYYYMMDD = (dateString: string) => {
    const parts = dateString.match(/(\d{4})年(\d{2})月(\d{2})日/);
    if (parts) {
      return `${parts[1]}${parts[2]}${parts[3]}`;
    }
    return '';
  };

  // 修改 formattedDate 的计算方式
  const formattedDate = useMemo(
    () => formatDateToYYYYMMDD(currentDate),
    [currentDate],
  );

  // 将 handleScheduleNotifications 移到组件内部
  const handleScheduleNotifications = useCallback(
    (scheduleList: GetScheduleData['scheduleList']) => {
      // 取消之前的所有通知
      // NotificationService.cancelAll();

      if (!Array.isArray(scheduleList) || scheduleList.length === 0) {
        console.log('No schedule data available');
        return;
      }

      // 获取当前日期的零点时间戳（用于日期比较）
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // 获取查询日期的零点时间戳
      const queryDate = new Date(
        formattedDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'),
      );
      queryDate.setHours(0, 0, 0, 0);

      // 如果查询的日期早于今天，不设置任何通知
      if (queryDate.getTime() < today.getTime()) {
        console.log('Schedule date is in the past, skipping notifications');
        return;
      }

      // 如果是未来日期，设置所有通知
      // 如果是当天，只设置还未到时间的通知
      // scheduleList.forEach(schedule => {
      //   if (!schedule.fullTime || !schedule.content) {
      //     return;
      //   }
      //
      //   try {
      //     const scheduleTime = new Date(schedule.fullTime);
      //     const now = new Date();
      //
      //     // 检查是否应该设置这个通知
      //     const shouldSetNotification =
      //       queryDate.getTime() > today.getTime() ||
      //       (queryDate.getTime() === today.getTime() &&
      //         scheduleTime.getTime() > now.getTime());
      //
      //     // if (shouldSetNotification) {
      //     //   // 使用 schedule.content 作为通知内容
      //     //   const notificationTitle = schedule.title || '课程提醒';
      //     //   const notificationContent = schedule.content;
      //     //
      //     //   // 设置系统通知
      //     //   NotificationService.scheduleFutureNotification(
      //     //     notificationTitle,
      //     //     notificationContent,
      //     //     scheduleTime,
      //     //   );
      //     //
      //     //   // 设置定时器来触发应用内通知
      //     //   const timeUntilNotification =
      //     //     scheduleTime.getTime() - now.getTime();
      //     //   setTimeout(() => {
      //     //     NotificationService.showNotification(
      //     //       notificationTitle,
      //     //       notificationContent,
      //     //     );
      //     //   }, timeUntilNotification);
      //     //
      //     //   console.log('Scheduled notification for:', {
      //     //     title: notificationTitle,
      //     //     content: notificationContent,
      //     //     time: scheduleTime.toLocaleString(),
      //     //     timeUntil:
      //     //       Math.floor(timeUntilNotification / 1000 / 60) + ' minutes',
      //     //   });
      //     // } else {
      //     //   console.log('Skipped past schedule:', {
      //     //     title: schedule.title,
      //     //     content: schedule.content,
      //     //     time: scheduleTime.toLocaleString(),
      //     //   });
      //     // }
      //   } catch (error) {
      //     console.error('Error scheduling notification:', error);
      //   }
      // });
    },
    [formattedDate], // 添加 formattedDate 作为依赖
  );

  const getScheduleList = useCallback(() => {
    // 在获取新数据之前先取消所有现有通知
    // NotificationService.cancelAll();

    getSchedule({date: formattedDate})
      .then(res => {
        console.log('Schedule data received:', res.data.scheduleList);
        setScheduleData(res.data.scheduleList);

        // 获取到新的日程数据后重新设置通知
        handleScheduleNotifications(res.data.scheduleList);
      })
      .catch(error => {
        console.error('Failed to fetch schedule:', error);
      });
  }, [formattedDate, handleScheduleNotifications]);

  // 修改 refreshSchedule 函数，确保刷新时也会重置通知
  const refreshSchedule = useCallback(() => {
    // 先取消所有通知
    // NotificationService.cancelAll();
    setCurrentDate(formatDate());
    // getScheduleList 会在 currentDate 更新后通过 useEffect 自动调用
  }, []);

  useEffect(() => {
    if (!rightIsOpen) {
      return;
    }
    // 清除旧通知并获取新日程
    // NotificationService.cancelAll();
    getScheduleList();
  }, [rightIsOpen, getScheduleList]);

  const toggleDrawer = useCallback(
    (side: 'left' | 'right') => {
      const isOpen = side === 'left' ? leftIsOpen : rightIsOpen;
      const slideAnim = side === 'left' ? leftSlideAnim : rightSlideAnim;
      const buttonOpacity =
        side === 'left' ? leftButtonOpacity : rightButtonOpacity;
      const setIsOpen = side === 'left' ? setLeftIsOpen : setRightIsOpen;

      if (isOpen) {
        animateDrawer(slideAnim, buttonOpacity, -1000, 1, () =>
          setIsOpen(false),
        );
      } else {
        animateDrawer(slideAnim, buttonOpacity, 0, 0, () => {
          setIsOpen(true);
          if (side === 'right') {
            refreshSchedule(); // 打开右侧抽屉时刷新日程
          }
        });
      }
    },
    [
      leftIsOpen,
      rightIsOpen,
      leftSlideAnim,
      rightSlideAnim,
      leftButtonOpacity,
      rightButtonOpacity,
      animateDrawer,
      refreshSchedule,
    ],
  );

  React.useEffect(() => {
    updateOverlay();
  }, [leftIsOpen, rightIsOpen, updateOverlay]);
  useEffect(() => {
    // const requestMicrophonePermission = async () => {
    //   try {
    //     const granted = await PermissionsAndroid.request(
    //       PermissionsAndroid.PERMISSIONS.RECORD_AUDIO,
    //       {
    //         title: 'Microphone Permission',
    //         message:
    //           'App needs access to your microphone for speech recognition.',
    //         buttonNeutral: 'Ask Me Later',
    //         buttonNegative: 'Cancel',
    //         buttonPositive: 'OK',
    //       },
    //     );
    //     if (granted === PermissionsAndroid.RESULTS.GRANTED) {
    //       console.log('Microphone permission granted');
    //     } else {
    //       console.log('Microphone permission denied');
    //     }
    //   } catch (err) {
    //     console.warn(err);
    //   }
    // };
    //
    // requestMicrophonePermission();

    // AINativeModules.init();

    const onRecognizingSubscription = AINativeModules.addListener(
      'onRecognizing',
      event => {
        console.log('onRecognizing event', event);
        leftWebviewRef.current?.sendMessage({
          eventName: 'onRecognizing',
          data: event.data,
        });
      },
    );

    const onRecognizedSubscription = AINativeModules.addListener(
      'onRecognized',
      event => {
        leftWebviewRef.current?.sendMessage({
          eventName: 'onRecognized',
          data: event.data,
        });
      },
    );

    return () => {
      onRecognizingSubscription.remove();
      onRecognizedSubscription.remove();
    };
  }, []);
  const goToPage = useCallback(
    (item: any) => {
      // 添加防御性检查
      if (!navigation || !item) {
        console.warn('Navigation or item is undefined');
        return;
      }

      // 记录日程点击
      try {
        logApi({
          useFunction: item.path,
          bizId: '',
        }).catch(err => console.error('Log API error:', err));
      } catch (e) {
        console.error('Log API error:', e);
      }

      try {
        if (item.clickTo === 1) {
          console.log('Navigating to:', item.path, item.params);

          if (item.path === 'InitialSubjectTest' && item.params?.recordId) {
            if (item.params.status === 'completed') {
              // @ts-ignore
              navigation.navigate('AnswerReport', {
                id: item.params.recordId,
              });
              return;
            }

            dispatch(
              fetchQuestionsByRecordId({
                generateRecordId: item.params.recordId,
              }),
            )
              .unwrap()
              .then(res => {
                if (res) {
                  // @ts-ignore
                  navigation.navigate('TestDetails', {
                    recordId: item.params.recordId,
                  });
                } else {
                  Toast.show({
                    text1: '获取题目失败,请稍后重试',
                    type: 'error',
                  });
                }
              })
              .catch(err => {
                console.error('Fetch questions error:', err);
                Toast.show({
                  text1: '获取题目失败,请稍后重试',
                  type: 'error',
                });
              })
              .finally(() => {
                hideGlobalLoading();
              });
          } else {
            // 添加导航状态检查
            if (navigation.isFocused()) {
              // @ts-ignore
              navigation.navigate(item.path, item.params || {});
            }
          }
        } else if (item.clickTo === 2) {
          IntentLauncher.startActivity({
            action: 'android.intent.action.MAIN',
            category: 'android.intent.category.LAUNCHER',
            packageName: 'com.jxw.launcher',
            className: 'com.jxw.launcher.ThemeActivity',
            flags: IntentConstant.FLAG_ACTIVITY_NEW_TASK,
          }).catch(err => console.error('Intent launcher error:', err));
        }
      } catch (error) {
        console.error('Navigation error:', error);
        Toast.show({
          text1: '页面跳转失败，请重试',
          type: 'error',
        });
      }
    },
    [navigation, dispatch],
  );
  const webEventHandle: middlewareType = useCallback(
    (eventName, _data, _res) => {
      switch (eventName) {
        case 'WebMounted': {
          console.log('WebMounted');
          setLeftLoading(false);
          break;
        }
        case 'goMenuRoute': {
          goToPage(_data);
          break;
        }
        // 保存当天聊天记录
        case 'saveChatRecord': {
          console.log('保存聊天记录', _data);
          break;
        }
        // 打开右侧抽屉
        case 'openScheduleDrawer': {
          console.log('打开右侧抽屉');
          toggleDrawer('right');
          break;
        }
        case 'startRecognize': {
          console.log('触发start');
          AINativeModules.startRecognize()
            .then((res: any) => {
              console.log('startRecognize res', res);
              leftWebviewRef.current?.sendMessage({
                eventName: 'recognizeStarted',
                data: null,
              });
            })
            .catch(error => {
              console.log('Failed to start recognize:', error);
            });
          break;
        }
        case 'stopRecognize': {
          console.log('触发stop');
          AINativeModules.stopRecognize()
            .then(() => {
              leftWebviewRef.current?.sendMessage({
                eventName: 'recognizeStopped',
                data: null,
              });
            })
            .catch(error => {
              console.log('Failed to stop recognize:', error);
            });
          break;
        }
      }
    },
    [goToPage, toggleDrawer],
  );
  const leftWebviewUrl = React.useMemo(
    () => `${AIWebviewURL}/AI/newChatUI`,
    [],
  );

  const memoizedWebView = useMemo(
    () => (
      <WebviewCustomerMemo
        ref={leftWebviewRef}
        eventHandle={webEventHandle}
        url={leftWebviewUrl}
        style={styles.webviewStyle}
      />
    ),
    [webEventHandle, leftWebviewUrl],
  );

  // 添加事件监听器
  useEffect(() => {
    const subscription = DeviceEventEmitter.addListener(
      'openScheduleDrawer',
      () => {
        if (!rightIsOpen) {
          toggleDrawer('right');
        }
      },
    );

    // 清理函数
    return () => {
      subscription.remove();
    };
  }, [rightIsOpen, toggleDrawer]);

  // 添加通知权限请求
  useEffect(() => {
    const requestPermission = async () => {
      // const hasPermission =
      // await NotificationService.requestNotificationPermission();
      // console.log('Has notification permission:', hasPermission);
    };
    requestPermission();
  }, []);

  // 监听通知点击事件
  useEffect(() => {
    const subscription = DeviceEventEmitter.addListener(
      'notificationClicked',
      notification => {
        if (notification.userInfo?.type === 'schedule') {
          // 处理日程通知点击，打开右侧抽屉
          if (!rightIsOpen) {
            toggleDrawer('right');
          }
        }
      },
    );

    return () => {
      subscription.remove();
    };
  }, [rightIsOpen, toggleDrawer]);

  return (
    <View style={styles.container} pointerEvents="box-none">
      <Animated.View
        style={[styles.overlay, {opacity: overlayOpacity}]}
        pointerEvents={leftIsOpen || rightIsOpen ? 'auto' : 'none'}>
        <TouchableOpacity
          style={StyleSheet.absoluteFill}
          onPress={closeDrawers}
          activeOpacity={1}
        />
      </Animated.View>
      {showLeftButton && (
        <Animated.View
          style={[
            styles.leftDrawer,
            {transform: [{translateY: leftSlideAnim}]},
          ]}>
          <TouchableOpacity onPress={() => toggleDrawer('left')}>
            <Image
              source={{
                uri: 'https://media.oss.leziedu.com/webapp/classroom/newPad/new_pad_ai_expand.png?t=1',
              }}
              style={styles.leftDrawerHeaderImage}
            />
          </TouchableOpacity>
          <View style={styles.leftDrawerContent}>
            {/* loading */}
            {leftLoading && (
              <View style={styles.loading}>
                <ActivityIndicator size="small" color="#000000" />
              </View>
            )}
            {memoizedWebView}
          </View>
        </Animated.View>
      )}

      <Animated.View
        style={[
          styles.rightDrawer,
          {transform: [{translateY: rightSlideAnim}]},
        ]}>
        <TouchableOpacity
          onPress={() => toggleDrawer('right')}
          style={styles.rightDrawerHeader}>
          <ImageBackground
            source={{
              uri: 'https://media.oss.leziedu.com/webapp/classroom/newPad/new_pad_rc_expand.png',
            }}
            style={styles.rightDrawerHeaderImage}
            resizeMode="contain">
            <View style={styles.dateContainer}>
              <Text style={styles.dateText}>{currentDate}</Text>
              <Image
                source={{
                  uri: 'https://media.oss.leziedu.com/webapp/classroom/newPad/new_pad_expand_icon.png',
                }}
                style={styles.collapseIcon}
              />
            </View>
          </ImageBackground>
        </TouchableOpacity>
        <View style={styles.rightDrawerContent}>
          <ScheduleTimeline
            scheduleList={scheduleData}
            onItemClick={() => {
              toggleDrawer('right');
            }}
          />
        </View>
      </Animated.View>

      {showLeftButton && (
        <Animated.View
          style={[styles.leftButtonContainer, leftButtonContainerStyle as any]}
          {...panResponderLeft.panHandlers}>
          <TouchableOpacity
            style={styles.toggleButton}
            onPress={() => toggleDrawer('left')}
            activeOpacity={0.7}>
            <Image
              source={{
                uri: 'https://media.oss.leziedu.com/webapp/classroom/newPad/new_pad_ai.png',
              }}
              style={styles.buttonImage}
            />
          </TouchableOpacity>
        </Animated.View>
      )}

      {showRightButton && (
        <Animated.View
          style={[
            styles.rightButtonContainer,
            rightButtonContainerStyle as any,
          ]}
          {...panResponderRight.panHandlers}>
          <TouchableOpacity
            style={styles.toggleButton}
            onPress={() => toggleDrawer('right')}
            activeOpacity={0.7}>
            <Image
              source={{
                uri: 'https://media.oss.leziedu.com/webapp/classroom/newPad/new_pad_richeng.png',
              }}
              style={styles.buttonImage}
            />
          </TouchableOpacity>
        </Animated.View>
      )}
    </View>
  );
}

const styles = createStyles({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    // backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 1002,
  },
  leftDrawer: {
    height: '97vh',
    position: 'absolute',
    left: '0.9rem',
    top: '0.9rem',
    right: '0.9rem', // 添加右侧 margin
    bottom: '0.9rem', // 添加底部 margin
    width: LEFT_DRAWER_WIDTH,
    zIndex: 1003,
    borderRadius: '0.6rem',
    overflow: 'hidden',
    display: 'flex', // 使用 flex 布
    flexDirection: 'column', // 垂直方向排列子元素
  },
  rightDrawer: {
    height: '97vh',
    position: 'absolute',
    right: '0.9rem',
    top: '0.9rem',
    bottom: '0.9rem',
    width: RIGHT_DRAWER_WIDTH,
    zIndex: 1003,
    borderRadius: '0.6rem',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  leftButtonContainer: {
    position: 'absolute',
    left: 0,
    top: 8,
    zIndex: 1003,
  } as ViewStyle,
  rightButtonContainer: {
    position: 'absolute',
    right: 0,
    top: 8,
    zIndex: 1003,
  } as ViewStyle,
  toggleButton: {
    width: '8.65rem',
    height: '3.5rem',
    resizeMode: 'contain',
  },
  buttonImage: {
    width: '8.65rem',
    height: '3.5rem',
    resizeMode: 'contain',
  },
  leftDrawerHeaderImage: {
    width: '100%',
    height: '3.8rem',
    resizeMode: 'contain',
  },
  rightDrawerHeader: {
    width: '100%',
    height: '3.8rem',
  },
  rightDrawerHeaderImage: {
    width: '100%',
    height: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    // paddingRight: '1rem',
  },
  dateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: '1rem',
  },
  dateText: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginRight: '0.5rem',
  },
  collapseIcon: {
    width: '0.8rem',
    height: '0.75rem',
    resizeMode: 'contain',
  },
  webviewBox: {
    flex: 1,
    width: RIGHT_DRAWER_WIDTH,
  },
  // 添加新的样式来包裹 WebviewCustomerMemo
  leftDrawerContent: {
    flex: 1, // 占据剩余空间
    overflow: 'hidden',
  },
  // 添加右侧抽屉内容式
  rightDrawerContent: {
    flex: 1,
    backgroundColor: '#fff',
  },
  // 添加新的样式
  leftDrawerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  leftDrawerHeaderImageContainer: {
    flex: 1,
  },
  webviewStyle: {
    height: '100%',
    flex: 1,
    width: '100%',
  },
  webviewContainer: {
    flex: 1,
    width: '100%',
  },
  loading: {
    backgroundColor: '#fff',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
